import { useEffect, useMemo, useState } from "react";
import type { Assignment, Project, AiProjectSummary } from "../services/api";
import { listAssignments, listProjects, updateAssignment } from "../services/api";
import { useAuth } from "../auth/AuthContext";
import { Modal } from "./Modal";

interface StatusOption {
  value: Assignment["status"];
  label: string;
  restricted?: boolean;
}

const STATUS_OPTIONS: StatusOption[] = [
  { value: "SELF_NOMINATED", label: "Self Nominated" },
  { value: "NOMINATED", label: "Nominated", restricted: true },
  { value: "SUBMITTED_TO_AWS", label: "Submitted to AWS", restricted: true },
  { value: "INTERVIEW", label: "Interview" },
  { value: "ACCEPTED", label: "Accepted", restricted: true },
  { value: "DECLINED", label: "Declined", restricted: true },
];

export function MyDashboardPage() {
  const { user, hasRole } = useAuth();
  const email = (user?.email ?? user?.username ?? "").toLowerCase();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [projectsById, setProjectsById] = useState<Record<string, Project>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alert, setAlert] = useState<{ type: "info" | "error"; message: string } | null>(null);
  const [updatingId, setUpdatingId] = useState<string | null>(null);
  const [pendingChange, setPendingChange] = useState<{ assignment: Assignment; nextStatus: Assignment["status"] } | null>(
    null,
  );
  const [statusNotes, setStatusNotes] = useState("");
  const [interviewAt, setInterviewAt] = useState("");
  const [interviewContact, setInterviewContact] = useState("");
  const [tempSelections, setTempSelections] = useState<Record<string, Assignment["status"]>>({});
  const [projectDetail, setProjectDetail] = useState<Project | null>(null);
  const projectDetailSummary = projectDetail ? (projectDetail.summary as AiProjectSummary | undefined) : undefined;
  const projectDetailNotes =
    projectDetailSummary?.notes ?? projectDetail?.notes ?? projectDetail?.description ?? "No additional notes";

  const isPrivileged = hasRole("admin") || hasRole("ops") || hasRole("root");

  useEffect(() => {
    if (!email) {
      return;
    }
    let cancelled = false;
    async function loadDashboard() {
      setLoading(true);
      setError(null);
      try {
        const [assignmentItems, projectItems] = await Promise.all([
          listAssignments({ person_email: email }),
          listProjects(),
        ]);
        if (cancelled) {
          return;
        }
        const projectMap = projectItems.reduce<Record<string, Project>>((acc, project) => {
          acc[project.project_id] = project;
          return acc;
        }, {});
        setAssignments(assignmentItems);
        setProjectsById(projectMap);
      } catch (err) {
        if (cancelled) {
          return;
        }
        setError(err instanceof Error ? err.message : "Unable to load your RR assignments right now.");
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }
    void loadDashboard();
    return () => {
      cancelled = true;
    };
  }, [email]);

  const visibleAssignments = useMemo(() => {
    return assignments.filter((assignment) => {
      const project = projectsById[assignment.project_id];
      return (project?.status ?? "").toUpperCase() !== "EXPIRED";
    });
  }, [assignments, projectsById]);

  const orderedAssignments = useMemo(() => {
    return [...visibleAssignments].sort((a, b) => {
      const aDate = a.updated_at ?? a.created_at ?? "";
      const bDate = b.updated_at ?? b.created_at ?? "";
      return bDate.localeCompare(aDate);
    });
  }, [visibleAssignments]);

  async function handleStatusChange(
    assignment: Assignment,
    nextStatus: Assignment["status"],
    details?: { notes?: string; interviewAt?: string; interviewContact?: string },
  ): Promise<boolean> {
    if (assignment.status === nextStatus) {
      return false;
    }

    const selectedOption = STATUS_OPTIONS.find((option) => option.value === nextStatus);
    if (selectedOption?.restricted && !isPrivileged) {
      return false;
    }

    setUpdatingId(assignment.assignment_id);
    setAlert(null);
    try {
      const payload: Record<string, unknown> = { status: nextStatus };
      if (details?.notes) {
        payload.status_notes = details.notes;
      }
      if (details?.interviewAt) {
        payload.interview_at = details.interviewAt;
      }
      if (details?.interviewContact) {
        payload.interview_contact = details.interviewContact;
      }
      const updated = await updateAssignment(assignment.assignment_id, payload);
      setAssignments((prev) =>
        prev.map((item) => (item.assignment_id === updated.assignment_id ? updated : item)),
      );
      setAlert({ type: "info", message: "Assignment status updated." });
      return true;
    } catch (err) {
      setAlert({
        type: "error",
        message: err instanceof Error ? err.message : "Unable to update the assignment status.",
      });
      return false;
    } finally {
      setUpdatingId(null);
    }
  }

  return (
    <section className="panel">
      <h2>My Dashboard</h2>
      <p className="helper-text">
        Track the AWS requisitions you are attached to. Update your status as you progress through the pipeline.
      </p>
      {alert && <div className={`alert ${alert.type === "error" ? "error" : "info"}`}>{alert.message}</div>}
      {error && <div className="alert error">{error}</div>}
      {loading ? (
        <p>Loading your assignments…</p>
      ) : orderedAssignments.length === 0 ? (
        <div className="empty">You have no active RR assignments yet.</div>
      ) : (
        <div className="table-wrapper">
          <table>
            <thead>
              <tr>
                <th>Requisition</th>
                <th>Status</th>
                <th>Updated</th>
              </tr>
            </thead>
            <tbody>
              {orderedAssignments.map((assignment) => {
                const project = projectsById[assignment.project_id];
                const projectTitle = project?.title ?? assignment.project_id;
                const updatedAt = assignment.updated_at ?? assignment.created_at ?? "";
                return (
                  <tr key={assignment.assignment_id}>
                    <td>
                      <button
                        type="button"
                        className="link-button"
                        onClick={() => project && setProjectDetail(project)}
                        disabled={!project}
                      >
                        <strong>
                          {assignment.project_id} – {projectTitle}
                        </strong>
                      </button>
                      <br />
                      <span className="helper-text">
                        AWS Role: {project?.aws_role ?? "Unknown"} · TW Archetype: {project?.tw_archetype ?? "Unknown"}
                      </span>
                    </td>
                    <td>
                      <select
                        value={tempSelections[assignment.assignment_id] ?? assignment.status}
                        onChange={(event) => {
                          const nextStatus = event.target.value as Assignment["status"];
                          if (!isPrivileged) {
                            setTempSelections((prev) => ({ ...prev, [assignment.assignment_id]: nextStatus }));
                            setPendingChange({ assignment, nextStatus });
                            setStatusNotes("");
                            setInterviewAt("");
                            setInterviewContact("");
                            return;
                          }
                          void handleStatusChange(assignment, nextStatus);
                        }}
                        disabled={updatingId === assignment.assignment_id}
                      >
                        {STATUS_OPTIONS.map((option) => (
                          <option
                            key={option.value}
                            value={option.value}
                            disabled={Boolean(option.restricted && !isPrivileged)}
                          >
                            {option.label}
                          </option>
                        ))}
                      </select>
                    </td>
                    <td>{updatedAt ? new Date(updatedAt).toLocaleString() : "—"}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {!isPrivileged && (
        <p className="helper-text" style={{ marginTop: "1rem" }}>
          Statuses marked as “staff only” are updated by Admin/Ops teams. Contact staffing if you need a change.
        </p>
      )}
      <Modal
        open={Boolean(pendingChange)}
        onClose={() => {
          if (pendingChange) {
            setTempSelections((prev) => {
              const copy = { ...prev };
              delete copy[pendingChange.assignment.assignment_id];
              return copy;
            });
          }
          setPendingChange(null);
        }}
        title="Add status context"
      >
        {pendingChange && (
          <form
            className="form-grid"
            onSubmit={async (event) => {
              event.preventDefault();
              const trimmedNotes = statusNotes.trim();
              if (!trimmedNotes) {
                setAlert({ type: "error", message: "Please provide notes for this status update." });
                return;
              }
              if (pendingChange.nextStatus === "INTERVIEW") {
                if (!interviewAt) {
                  setAlert({ type: "error", message: "Please provide the interview date/time." });
                  return;
                }
                if (!interviewContact.trim()) {
                  setAlert({ type: "error", message: "Please provide a contact for the interview." });
                  return;
                }
              }
              const interviewIso =
                pendingChange.nextStatus === "INTERVIEW" && interviewAt
                  ? new Date(interviewAt).toISOString()
                  : undefined;
              const success = await handleStatusChange(pendingChange.assignment, pendingChange.nextStatus, {
                notes: trimmedNotes,
                interviewAt: interviewIso,
                interviewContact: interviewContact.trim() || undefined,
              });
              if (success) {
                setPendingChange(null);
                setStatusNotes("");
                setInterviewAt("");
                setInterviewContact("");
                setTempSelections((prev) => {
                  const copy = { ...prev };
                  delete copy[pendingChange.assignment.assignment_id];
                  return copy;
                });
              }
            }}
          >
            <label className="span-2">
              Notes
              <textarea
                required
                rows={4}
                value={statusNotes}
                onChange={(event) => setStatusNotes(event.target.value)}
                placeholder="Add context for staffing (required)"
              />
            </label>
            {pendingChange.nextStatus === "INTERVIEW" && (
              <>
                <label>
                  Interview date & time
                  <input
                    type="datetime-local"
                    required
                    value={interviewAt}
                    onChange={(event) => setInterviewAt(event.target.value)}
                  />
                </label>
                <label>
                  Interview contact
                  <input
                    type="text"
                    required
                    value={interviewContact}
                    onChange={(event) => setInterviewContact(event.target.value)}
                    placeholder="Who is hosting the interview?"
                  />
                </label>
              </>
            )}
            <div className="form-actions span-2">
              <button type="submit" className="primary" disabled={updatingId === pendingChange.assignment.assignment_id}>
                Submit update
              </button>
              <button
                type="button"
                className="ghost"
                onClick={() => {
                  if (pendingChange) {
                    setTempSelections((prev) => {
                      const copy = { ...prev };
                      delete copy[pendingChange.assignment.assignment_id];
                      return copy;
                    });
                  }
                  setPendingChange(null);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
      </Modal>
      <Modal open={Boolean(projectDetail)} onClose={() => setProjectDetail(null)} title="Requisition details">
        {projectDetail && (
          <div className="detail-grid">
            <div>
              <h3>{projectDetail.title}</h3>
              <p className="helper-text">{projectDetail.project_id}</p>
            </div>
            <dl className="detail-list">
              <div>
                <dt>AWS Role</dt>
                <dd>{projectDetail.aws_role ?? "—"}</dd>
              </div>
              <div>
                <dt>TW Archetype</dt>
                <dd>{projectDetail.tw_archetype ?? "—"}</dd>
              </div>
              <div>
                <dt>Status</dt>
                <dd>{projectDetail.status ?? "—"}</dd>
              </div>
              <div>
                <dt>Delivery Unit</dt>
                <dd>{projectDetail.delivery_unit ?? "—"}</dd>
              </div>
              <div>
                <dt>Start Date</dt>
                <dd>{projectDetail.start_date ?? "TBD"}</dd>
              </div>
              <div>
                <dt>End Date</dt>
                <dd>{projectDetail.end_date ?? "TBD"}</dd>
              </div>
              <div className="span-2">
                <dt>Notes</dt>
                <dd>{projectDetailNotes}</dd>
              </div>
            </dl>
          </div>
        )}
      </Modal>
    </section>
  );
}
